import { useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import debounce from 'lodash.debounce';
import { v4 as uuid } from 'uuid';

import { useLocale } from 'react-aria';
import { useI18n } from '../../../../hooks/useI18nFormatters';

import { appsAPI } from '../../../../lib/appsAPI';
import { ToastManager } from '../../../../ToastManager';

import { useDriversByOwnerUri } from '../../../../store/drivers/useDrivers.ts';
import { useApi } from '../../../../store/SelectedHomeyStore.js';
import { useAppsById } from '../../../../store/apps/useApps.ts';

import { theme } from '../../../../theme/theme';
import { su } from '../../../../theme/functions/su';
import { animationFade } from '../../../../theme/animations/animationFade';
import { HomeyColors } from '../../../../theme/HomeyColors';

import { ExternalLink } from '../../../common/link/ExernalLink';
import { Icon } from '../../../common/Icon';
import { ListBox } from '../../../list-box/ListBox';
import { SearchField } from '../../../common/search-field/SearchField';

import { AppItem } from './AppItem';
import { GridSection } from './GridSection';

import { iconPersonGroupFill } from '../../../../theme/icons/interface/person-group-fill/person-group-fill';
import { iconCheckmarkSealFill } from '../../../../theme/icons/interface/checkmark-seal-fill/checkmark-seal-fill';
import { iconSquareArrowUpRight } from '../../../../theme/icons/interface/square-arrow-up-right/square-arrow-up-right';
import { iconSearch } from '../../../../theme/icons/interface/magnifying-glass/magnifying-glass';

export function SearchView(props) {
  const { locale } = useLocale();
  const { i18n } = useI18n();

  const instanceRef = useRef({});
  const searchFieldRef = useRef();

  const { api } = useApi();
  const apps = useAppsById();
  const drivers = useDriversByOwnerUri();

  const [state, setState] = useState({
    officialApps: null,
    communityApps: null,
    localApps: null,
    homeyApps: null,
    isSearch: false,
  });

  useEffect(() => {
    if (drivers.byOwnerUri != null && searchFieldRef.current?.value.length === 0) {
      const { 'homey:manager:vdevice': homeyApps, ...localApps } = drivers.byOwnerUri;

      setState({
        officialApps: null,
        communityApps: null,
        localApps: {
          ...localApps,
        },
        homeyApps: homeyApps || null,
        isSearch: false,
      });
    }
  }, [drivers.byOwnerUri]);

  const handleChange = useMemo(() => {
    return debounce(async (value) => {
      if (drivers.byOwnerUri == null) {
        return;
      }

      const id = (instanceRef.current.pendingId = uuid());
      const { 'homey:manager:vdevice': homeyApps, ...localApps } = drivers.byOwnerUri;

      if (value.length === 0) {
        setState({
          officialApps: null,
          communityApps: null,
          localApps: {
            ...localApps,
          },
          homeyApps: homeyApps || null,
          isSearch: false,
        });

        return;
      }

      try {
        const storeApps = await appsAPI.searchApps({
          query: value,
          homeyVersion: api.homeyVersion,
          homeyPlatform: api.homey.platform,
          homeyPlatformVersion: String(api.homey.platformVersion),
          homeyModel: api.homey.model,
          language: locale === 'ar' ? 'en' : locale, // fallback to english for Arabic languages todo remove this when the website supports Arabic language
        });

        if (id !== instanceRef.current.pendingId) {
          return;
        }

        const officialApps = {};
        const communityApps = {};

        storeApps.forEach((app) => {
          if (app.driversCount === 0) {
            return;
          }

          const appUri = `homey:app:${app.id}`;

          if (app.authorVerified) {
            officialApps[appUri] = drivers.byOwnerUri[appUri] ?? { appObj: app };
          } else {
            communityApps[appUri] = drivers.byOwnerUri[appUri] ?? { appObj: app };
          }
        });

        setState({
          officialApps: officialApps,
          communityApps: communityApps,
          localApps: {
            ...localApps,
          },
          homeyApps: homeyApps ?? null,
          isSearch: true,
        });
      } catch (error) {
        ToastManager.handleError(error);
      }
    }, 300);
  }, [api, locale, drivers.byOwnerUri]);

  const items = useMemo(() => {
    function formatApps(appsObject) {
      return Object.entries(appsObject ?? {}).map(([ownerUri, drivers]) => {
        const owner = {
          name: null,
          color: null,
          iconUrl: null,
        };

        if (ownerUri === 'homey:manager:vdevice') {
          owner.name = 'Homey';
          owner.color = '#333333';
        }

        const app = apps.byId?.[ownerUri.substring('homey:app:'.length)];

        if (app != null) {
          const ownerIconId = app.iconObj?.id;
          owner.iconUrl = `https://icons-cdn.athom.com/${ownerIconId}.svg`;
          owner.name = app.name;
          owner.color = app.color ?? '#333333';
        }

        if (drivers.appObj) {
          owner.name = drivers.appObj.name;
          owner.color = drivers.appObj.brandColor;
          owner.iconUrl = drivers.appObj.icon;
        }

        return {
          key: ownerUri,
          type: 'app',
          textValue: owner.name,
          context: {
            owner: owner,
          },
        };
      });
    }

    function formatHomeyApps(appsObject) {
      return Object.entries(appsObject ?? {})
        .filter(([_, driver]) => driver.pair !== false) // Filter out drivers where pair is false
        .sort((a, b) => a[1].name.toLowerCase().localeCompare(b[1].name.toLowerCase()))
        .map(([uri, driver]) => {
          const owner = {
            name: driver.name,
            color:
              uri.startsWith('homey:manager:vdevice:virtual_') ||
              uri.startsWith('homey:virtualdrivervirtual')
                ? theme.color.system_experiments
                : HomeyColors.getColorForUri(uri),
            iconUrl: `https://icons-cdn.athom.com/${driver.iconObj?.id}.svg`,
          };

          return {
            key: uri,
            type: 'app',
            textValue: driver.name,
            context: {
              owner: owner,
            },
          };
        });
    }

    const officialApps = formatApps(state.officialApps);
    const communityApps = formatApps(state.communityApps);
    const localApps = formatApps(state.localApps);
    const homeyApps = formatHomeyApps(state.homeyApps);

    return {
      official: [
        {
          key: 'official',
          children: officialApps,
        },
      ],
      community: [
        {
          key: 'community',
          children: communityApps,
        },
      ],
      local: [
        {
          key: 'local',
          children: localApps,
        },
      ],
      homeyApps: [
        {
          key: 'technologies',
          children: homeyApps,
        },
      ],
    };
  }, [state.officialApps, state.communityApps, state.localApps, state.homeyApps, apps.byId]);

  function onSelectionChange(keys, listState) {
    const item = listState.collection.getItem(keys[0]);

    props.onDriverOwnerUriSelect({
      uri: item?.key ?? null,
    });
  }

  function renderLabel({ label, props }) {
    return (
      <SearchView.Label {...props}>
        {label.iconUrl && (
          <Icon url={label.iconUrl} size={theme.icon.size_default} color={theme.icon.color_dark} />
        )}
        {label.text}
      </SearchView.Label>
    );
  }

  function renderSection() {
    return <GridSection />;
  }

  function renderItem() {
    return <AppItem />;
  }

  const isReady = drivers.isLoading === false && drivers.error == null;

  useEffect(() => {
    requestAnimationFrame(() => {
      searchFieldRef.current.focus();
    });
  }, []);

  return (
    <SearchView.Root>
      <SearchView.SearchField
        ref={searchFieldRef}
        aria-label="Search"
        borderRadius={theme.borderRadius.default}
        placeholder={i18n.messageFormatter('pair.searchPlaceholder')}
        onChange={handleChange}
        onKeyDown={(event) => {
          // If it was empty and escape it pressed propagate event so that
          // overlays can be closed with keyboard.
          if (event.key === 'Escape' && event.currentTarget.value === '') {
            event.continuePropagation();
          }
        }}
      />

      {isReady === true && state.isSearch === true && (
        <SearchView.SearchResults>
          {items.official[0].children.length !== 0 && (
            <ListBox
              label={{
                iconUrl: api.homey.platform === 'local' ? iconCheckmarkSealFill : null,
                text:
                  api.homey.platform === 'local'
                    ? i18n.messageFormatter('pair.officialTitle')
                    : i18n.messageFormatter('pair.searchResults'),
              }}
              items={items.official}
              onSelectionChange={onSelectionChange}
              renderLabel={renderLabel}
              renderSection={renderSection}
              renderItem={renderItem}
            />
          )}

          {items.community[0].children.length !== 0 && (
            <ListBox
              label={{
                iconUrl: iconPersonGroupFill,
                text: i18n.messageFormatter('pair.communityTitle'),
              }}
              items={items.community}
              onSelectionChange={onSelectionChange}
              renderLabel={renderLabel}
              renderSection={renderSection}
              renderItem={renderItem}
            />
          )}

          {items.official[0].children.length === 0 && items.community[0].children.length !== 0 && (
            <>
              <SearchView.Label>
                <Icon
                  url={iconCheckmarkSealFill}
                  size={theme.icon.size_default}
                  color={theme.icon.color_dark}
                />
                {i18n.messageFormatter('pair.officialTitle')}
              </SearchView.Label>
              <SearchView.NoResultsText>
                {i18n.messageFormatter('pair.noOfficialAppsFound', {
                  requestABrand: (
                    <SearchView.Link
                      key={'request-a-brand'}
                      url={'https://request-a-brand.homey.app/boards/request-a-brand'}
                    >
                      {i18n.messageFormatter('pair.requestABrand')}
                      <Icon
                        url={iconSquareArrowUpRight}
                        size={theme.icon.size_small}
                        color={theme.icon.color_blue}
                      />
                    </SearchView.Link>
                  ),
                })}
              </SearchView.NoResultsText>
            </>
          )}

          {items.official[0].children.length === 0 && items.community[0].children.length === 0 && (
            <SearchView.NoResults>
              <Icon url={iconSearch} size={'48px'} color={theme.icon.color_light} />
              <SearchView.NoResultsTitle>
                {i18n.messageFormatter('pair.noSearchResults')}
              </SearchView.NoResultsTitle>
              <SearchView.NoResultsText>
                {i18n.messageFormatter('pair.noAppsFound', {
                  requestABrand: (
                    <SearchView.Link
                      key={'request-a-brand'}
                      url={'https://request-a-brand.homey.app/boards/request-a-brand'}
                    >
                      {i18n.messageFormatter('pair.requestABrand')}
                      <Icon
                        url={iconSquareArrowUpRight}
                        size={theme.icon.size_small}
                        color={theme.icon.color_blue}
                      />
                    </SearchView.Link>
                  ),
                })}
              </SearchView.NoResultsText>
            </SearchView.NoResults>
          )}
        </SearchView.SearchResults>
      )}

      {isReady === true && state.isSearch === false && (
        <SearchView.SearchResults>
          <ListBox
            label={{ text: i18n.messageFormatter('pair.technologiesTitle') }}
            items={items.homeyApps}
            onSelectionChange={onSelectionChange}
            renderLabel={renderLabel}
            renderSection={renderSection}
            renderItem={renderItem}
          />

          {items.local[0].children.length !== 0 && (
            <ListBox
              label={{ text: i18n.messageFormatter('pair.brandsTitle') }}
              items={items.local}
              onSelectionChange={onSelectionChange}
              renderLabel={renderLabel}
              renderSection={renderSection}
              renderItem={renderItem}
            />
          )}
        </SearchView.SearchResults>
      )}
    </SearchView.Root>
  );
}

SearchView.Root = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  animation: ${animationFade.in} ${theme.duration.slow} ease-out 1 forwards;
`;

SearchView.SearchField = styled(SearchField)`
  flex: 0 0 auto;

  ${SearchField.Input} {
    height: ${theme.input.height_large};
    line-height: ${theme.input.height_large};
  }
`;

SearchView.SearchResults = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
`;

SearchView.Label = styled.h3`
  display: inline-grid;
  grid-auto-flow: column;
  grid-gap: 8px;
  align-items: center;
  justify-content: start;
  width: 100%;
  margin: ${su(4, 0, 2)};
  line-height: 30px;

  &:not(:first-of-type) {
    margin-top: ${su(1)};
    padding-top: ${su(2)};
    border-top: 1px solid ${theme.color.line};
  }
`;

SearchView.EmptyMessage = styled.div`
  color: ${theme.color.text_light};
`;

SearchView.Link = styled(ExternalLink)`
  display: inline-grid;
  grid-auto-flow: column;
  grid-gap: 5px;
  align-items: center;
  color: ${theme.color.text_link};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

SearchView.NoResults = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  white-space: pre-wrap;
  padding-bottom: ${su(5)};
`;

SearchView.NoResultsTitle = styled.h2`
  margin: 24px 0 8px;
  color: ${theme.color.text};
`;

SearchView.NoResultsText = styled.p`
  margin: 0;
  color: ${theme.color.text_light};
  line-height: 1.5;
`;
